import { useState, useEffect, useCallback } from 'react';
import { Search, Download, X, FileText, Filter } from 'lucide-react';
import { api, API_BASE, ensureFreshToken } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';

const inputCls =
  'bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none focus:border-blue-500';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

// Couleurs par catégorie pour le badge
const CAT_COLOR = {
  Auth: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  User: 'bg-violet-500/15 border-violet-500/30 text-violet-400',
  Group: 'bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-400',
  Permission: 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400',
  Client: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400',
  Site: 'bg-teal-500/15 border-teal-500/30 text-teal-400',
  Provision: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
  Agent: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  Command: 'bg-orange-500/15 border-orange-500/30 text-orange-400',
  AlertConfig: 'bg-pink-500/15 border-pink-500/30 text-pink-400',
  WindowsUpdate: 'bg-sky-500/15 border-sky-500/30 text-sky-400',
};

const CategoryBadge = ({ cat }) => (
  <span
    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
      CAT_COLOR[cat] || 'bg-slate-500/15 border-slate-500/30 text-muted'
    }`}
  >
    {cat || '—'}
  </span>
);

const StatusDot = ({ ok }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-ok' : 'bg-crit'}`}
    title={ok ? 'Succès' : 'Échec'}
  />
);

export function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Filtres
  const [category, setCategory] = useState('');
  const [user, setUser] = useState('');
  const [textQuery, setTextQuery] = useState('');
  const [success, setSuccess] = useState(''); // '', 'true', 'false'
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Catégories disponibles (chargées une fois)
  const [categories, setCategories] = useState([]);

  // Tri client-side sur la page courante (50 lignes max)
  const { sortRows, headerProps, arrow } = useTableSort('timestamp', 'desc');

  // Construit les query params à partir des filtres courants
  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (category) p.set('category', category);
    if (user) p.set('user', user);
    if (textQuery) p.set('q', textQuery);
    if (success !== '') p.set('success', success);
    if (from) p.set('from', new Date(from).toISOString());
    if (to) p.set('to', new Date(to).toISOString());
    return p;
  }, [category, user, textQuery, success, from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = buildQuery();
    p.set('page', String(page));
    p.set('pageSize', String(PAGE_SIZE));
    const r = await api.get('/audit?' + p.toString());
    if (r) {
      setLogs(r.logs || []);
      setTotalCount(r.totalCount || 0);
    }
    setLoading(false);
  }, [buildQuery, page]);

  useEffect(() => {
    api.get('/audit/categories').then((r) => {
      if (Array.isArray(r)) setCategories(r);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Reset à page 1 quand on change un filtre
  const onFilterChange = (setter) => (e) => {
    setter(e.target?.value ?? e);
    setPage(1);
  };

  const clearFilters = () => {
    setCategory('');
    setUser('');
    setTextQuery('');
    setSuccess('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const exportCsv = () => {
    const p = buildQuery();
    const url = API_BASE + '/audit/export?' + p.toString();
    // Browser navigation download — passe le token via Authorization header
    ensureFreshToken()
      .then((token) => fetch(url, { headers: { Authorization: 'Bearer ' + token } }))
      .then((r) => r.blob())
      .then((blob) => {
        const dl = document.createElement('a');
        dl.href = URL.createObjectURL(blob);
        dl.download = `audit-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
        document.body.appendChild(dl);
        dl.click();
        dl.remove();
      });
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasFilters = category || user || textQuery || success !== '' || from || to;

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-[22px] font-bold text-fg flex items-center gap-2">
          <FileText size={20} strokeWidth={1.75} className="text-blue-400" /> Journal d'audit
        </h1>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            className="bg-blue-500 border-none rounded-lg px-3.5 py-2 text-white text-xs font-semibold cursor-pointer hover:bg-blue-600 inline-flex items-center gap-1.5"
          >
            <Download size={14} strokeWidth={1.75} /> Exporter CSV
          </button>
        </div>
      </div>

      {/* Bandeau de filtres */}
      <div className="bg-card border border-rule rounded-2xl px-5 py-4 mb-4">
        <div className="flex items-center gap-2 mb-3 text-fg-2 text-xs font-bold uppercase tracking-wider">
          <Filter size={13} strokeWidth={1.75} /> Filtres
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto bg-transparent border border-rule-3 rounded px-2 py-0.5 text-muted text-[10px] cursor-pointer hover:bg-rule inline-flex items-center gap-1"
            >
              <X size={11} strokeWidth={1.75} /> Réinitialiser
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="relative">
            <Search
              size={14}
              strokeWidth={1.75}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2 pointer-events-none"
            />
            <input
              placeholder="Recherche (action, cible, détails)…"
              value={textQuery}
              onChange={onFilterChange(setTextQuery)}
              className={`${inputCls} w-full pl-9`}
            />
          </div>

          <select
            value={category}
            onChange={onFilterChange(setCategory)}
            className={`${inputCls} cursor-pointer w-full`}
          >
            <option value="">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <input
            placeholder="Utilisateur (email)"
            value={user}
            onChange={onFilterChange(setUser)}
            className={`${inputCls} w-full`}
          />

          <select
            value={success}
            onChange={onFilterChange(setSuccess)}
            className={`${inputCls} cursor-pointer w-full`}
          >
            <option value="">Succès et échecs</option>
            <option value="true">Succès uniquement</option>
            <option value="false">Échecs uniquement</option>
          </select>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-2 whitespace-nowrap">Du</span>
            <input
              type="datetime-local"
              value={from}
              onChange={onFilterChange(setFrom)}
              className={`${inputCls} flex-1`}
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-2 whitespace-nowrap">Au</span>
            <input
              type="datetime-local"
              value={to}
              onChange={onFilterChange(setTo)}
              className={`${inputCls} flex-1`}
            />
          </div>
        </div>
      </div>

      {/* Compteur résultats */}
      <div className="text-[11px] text-muted-2 mb-2.5">
        {loading
          ? 'Chargement…'
          : `${totalCount.toLocaleString('fr-FR')} entrée${totalCount > 1 ? 's' : ''} • Page ${page} / ${totalPages}`}
      </div>

      {/* Tableau */}
      <div className="bg-card border border-rule rounded-2xl overflow-hidden">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-sidebar">
              {[
                { key: 'success', label: '', w: 'w-[40px]' },
                { key: 'timestamp', label: 'Date', w: 'w-[160px]' },
                { key: 'category', label: 'Catégorie', w: 'w-[110px]' },
                { key: 'userEmail', label: 'Utilisateur', w: 'w-[200px]' },
                { key: 'action', label: 'Action', w: '' },
                { key: 'targetName', label: 'Cible', w: 'w-[180px]' },
                { key: 'ipAddress', label: 'IP', w: 'w-[130px] font-mono' },
              ].map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap ${c.w}`}
                  {...headerProps(c.key)}
                >
                  {c.label}
                  {arrow(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-muted-2 text-xs">
                  {loading ? 'Chargement…' : hasFilters ? 'Aucun résultat pour ces filtres' : 'Aucune entrée'}
                </td>
              </tr>
            ) : (
              sortRows(logs, (log, key) => {
                if (key === 'timestamp') return log.timestamp ? new Date(log.timestamp) : null;
                return log[key];
              }).map((log) => (
                <tr key={log.id} className={`border-b border-sidebar ${!log.success ? 'bg-crit/[.03]' : ''}`}>
                  <td className="px-3 py-2 text-center">
                    <StatusDot ok={log.success} />
                  </td>
                  <td className="px-3 py-2 text-fg-2 font-mono text-[11px] whitespace-nowrap">
                    {fmtDate(log.timestamp)}
                  </td>
                  <td className="px-3 py-2">
                    <CategoryBadge cat={log.category} />
                  </td>
                  <td className="px-3 py-2 text-fg-2 truncate" title={log.userEmail}>
                    {log.userEmail || '—'}
                  </td>
                  <td className="px-3 py-2 text-fg-2">{log.action}</td>
                  <td className="px-3 py-2 text-muted truncate" title={log.targetName}>
                    {log.targetName || '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-2 font-mono text-[10.5px]">{log.ipAddress || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-1.5 mt-4">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className={`bg-rule border-none rounded-md px-3.5 py-1.5 text-xs font-semibold ${
              page === 1 ? 'text-muted-3 cursor-default' : 'text-fg-2 cursor-pointer hover:bg-rule-2'
            }`}
          >
            ← Précédent
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            let p = i + 1;
            if (totalPages > 7) {
              if (page < 5) p = i + 1;
              else if (page > totalPages - 4) p = totalPages - 6 + i;
              else p = page - 3 + i;
            }
            return (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`border-none rounded-md px-2.5 py-1.5 text-xs font-semibold min-w-[32px] cursor-pointer ${
                  p === page ? 'bg-blue-500 text-white' : 'bg-rule text-muted hover:bg-rule-2'
                }`}
              >
                {p}
              </button>
            );
          })}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className={`bg-rule border-none rounded-md px-3.5 py-1.5 text-xs font-semibold ${
              page >= totalPages
                ? 'text-muted-3 cursor-default'
                : 'text-fg-2 cursor-pointer hover:bg-rule-2'
            }`}
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}
